import {EventBus} from '@mintdata-wsp/common/bus/EventBus';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {SaveDesignRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/SaveDesignRequest';
import {SaveDesignResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/SaveDesignResponse';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {DesignRef} from '@mintdata-wsp/common/state/ui/publish/PublishState';
import {
    DesignState,
    DesignStateFromJsonCtx,
    IDesignStateSnapshot
} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {InitEvent, SyncDesignEvent} from '@mintdata-wsp/wsp/controllers/multiplayer/MultiplayerController';
import {ISyncConnection, SyncClient} from '@mintdata-wsp/wsp/controllers/multiplayer/SyncClient';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Category, Logger} from '@mintdata/mint-kit/api/log/mk';
import throttle from 'lodash/throttle';
import {action, toJS} from 'mobx';
import {diff} from '@mintdata-wsp/thirdparty/jiff/jiff';
import {SpreadsheetsState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SpreadsheetsState';
import {BindingState} from '@mintdata-wsp/common/state/ui/visualDesigner/BindingState';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {Sheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {SpreadsheetViewState} from '@mintdata-wsp/common/state/ui/visualDesigner/SpreadsheetViewState';
import {IJsonPatch} from '@mintdata-wsp/thirdparty/jiff/lib/jsonPatch';

const logger = Logger.getLogger(Category.Multiplayer);

export class LoadDesignResult
{
    constructor(public readonly designName:string,
                public readonly uuid:string,
                public readonly version:number,
                public readonly designStateSnapshot:IDesignStateSnapshot)
    {
    }
}

export class DesignSyncController
{
    private _syncConnection:SyncConnection;

    private _syncClient:SyncClient;

    public async connect(designRef:DesignRef, isMultiplayerMode:boolean):Promise<LoadDesignResult>
    {
        const {projectId, designId} = designRef;
        this.disconnect();

        this._syncConnection = isMultiplayerMode
            ? new MultiplayerConnection(projectId, designId)
            : new SingleplayerConnection(designRef);

        this._syncClient = new SyncClient(this._syncConnection);

        const result = await this._syncConnection.connect(this._syncClient);
        return result;
    }

    public disconnect()
    {
        if( this._syncClient )
        {
            this._syncClient.disconnect();
            this._syncClient = null;
        }

        if( this._syncConnection )
        {
            this._syncConnection.close();
            this._syncConnection = null;
        }
    }

    @action.bound
    public async syncGuideDesign(newDesign:any):Promise<void>
    {
        const {props: guideProps} = controllers.ui.guide.getGuideState();

        if( guideProps )
        {
            newDesign.guideProps = toJS(guideProps);
        }

        console.log('%c syncGuideDesign: step, newDesign =', 'background:#0f0;color:#000;', guideProps.lastOpenedGuidePage, newDesign);

        this._syncClient.syncGuideDesign(newDesign);
    }

    public throttledSyncWholeDesign = throttle(this.syncWholeDesign, 500);

    @action.bound
    public async syncWholeDesign():Promise<void>
    {
        const openDesign = controllers.ui.openDesign.getOpenDesign();

        if( !openDesign || !openDesign.designState || !openDesign.initialized || !this._syncClient )
            return;

        const currentDesign:IDesignStateSnapshot = DesignState.toJson(openDesign.designState);

        const {props: guideProps} = controllers.ui.guide.getGuideState();

        if( guideProps )
        {
            currentDesign.guideProps = toJS(guideProps);
        }

        this._syncClient.onChangedFromClient(currentDesign);
    }

    @action.bound
    public async reSyncDesign():Promise<boolean>
    {
        const openDesign = controllers.ui.openDesign.getOpenDesign();

        if( !openDesign || !openDesign.designState || !openDesign.initialized )
            return false;

        return this._syncClient.reSyncToServer();
    }

    public undo()
    {
        if( !this._syncClient )
            return;

        this._syncClient.undo();
    }

    public redo()
    {
        if( !this._syncClient )
            return;

        this._syncClient.redo();
    }

    public clearUndoStack()
    {
        if( !this._syncClient )
            return;

        this._syncClient.clearUndoStack();
    }

    public get isUndoAllowed():boolean
    {
        return this._syncClient && this._syncClient.isUndoAllowed;
    }

    public get isRedoAllowed():boolean
    {
        return this._syncClient && this._syncClient.isRedoAllowed;
    }
}

abstract class SyncConnection implements ISyncConnection
{
    protected readonly projectId:number;
    protected readonly designId:number;

    protected syncClient:SyncClient;
    protected connected:boolean = true;

    protected constructor(projectId:number, designId:number)
    {
        this.projectId = projectId;
        this.designId = designId;
    }

    abstract sendSyncToServer(syncEvent:SyncDesignEvent, currentDesign:any):Promise<boolean>;

    @action.bound
    public setActiveDesign(newDesign:IDesignStateSnapshot)
    {
        const {openDesign: openDesignCtrl} = controllers.ui;

        const openDesign = openDesignCtrl.getOpenDesign();
        
        if( !openDesign || !openDesign.initialized )
            return;

        try
        {
            const ctx = new DesignStateFromJsonCtx();

            const newState = DesignState.fromJson(newDesign, openDesign.designState, DesignState.toJson(openDesign.designState), ctx);

            openDesignCtrl.setDesignState(newState);

            openDesignCtrl.processDesignStateFromJsonCtx(ctx);
        }
        catch( error )
        {
            logger.error('%cSERVER -> CLIENT :: Can\'t set new design:', 'color: red', error);
        }
    }

    @action
    public patchDesign(newDesign:any, appliedPatches?:IJsonPatch[])
    {
        const {openDesign: openDesignCtrl, activeUsersSync, surfaceArea, surfaceBoxReparenting} = controllers.ui;

        const openDesign = openDesignCtrl.getOpenDesign();
        if( !openDesign || !openDesign.initialized )
            return;

        const {designState} = openDesign;

        const designStateSnapshot = DesignState.toJson(designState);

        const ctx = new DesignStateFromJsonCtx();

        let startTime = performance.now();

        const stateDiff = appliedPatches || diff(designStateSnapshot, newDesign, {invertible: false});

        // console.debug('stateDiff took', performance.now() - startTime, 'ms');

        if( !stateDiff.length )
            return;

        const allPaths = stateDiff.map(patch => patch.path);

        const surfacePagePatchPaths = allPaths.filter(path => path.match(/\/surface\/pages\/(.*)\/artboard\/.*/));
        const surfaceOtherPaths = allPaths.filter(path => path.match(/\/surface\/(?!pages).*/));
        const workbookSheetCRUDPaths = allPaths.filter(path => path.match(/^\/spreadsheets\/workbooks\/(.*)\/runtime\/sheets\/(\d+)?(\/id|\/name|\/columnNames)?$/));
        const workbookViewPatchPaths = allPaths.filter(path => path.match(/^\/spreadsheets\/workbooks\/(.*)\/view\/.*/));
        const workbookSheetPatchPaths = allPaths.filter(path => path.match(/^\/spreadsheets\/workbooks\/(.*)\/runtime\/sheets\/(\d+)\/(slots|slotLocationToSlot)\/.*/));
        const workbookOtherPaths = allPaths.filter(path => path.match(/^\/spreadsheets\/workbooks\/(.*)\/runtime\/(?!sheets).*/));
        const bindingPatchPaths = allPaths.filter(path => path.match(/\/bindings\/.*/));

        const knownPatchesTotal =
            + surfacePagePatchPaths.length
            + surfaceOtherPaths.length
            + workbookSheetPatchPaths.length
            + workbookSheetCRUDPaths.length
            + workbookViewPatchPaths.length
            + workbookOtherPaths.length
            + bindingPatchPaths.length;

        if( stateDiff.length === knownPatchesTotal )
        {
            if( surfaceOtherPaths.length )
            {
                designState.surface = SurfaceState.fromJson(newDesign.surface, designState.surface, null, ctx);
            }
            else if( surfacePagePatchPaths.length )
            {
                const pageIds:string[] = [];

                for( const path of surfacePagePatchPaths )
                {
                    const pageId = path.match(/\/surface\/pages\/(.*)\/artboard\/.*/)[1];
                    if( pageId && !pageIds.includes(pageId))
                        pageIds.push(pageId);
                }

                setTimeout(() => activeUsersSync.clearBoxMoveState());

                for( const pageId of pageIds)
                {
                    const currentPageState = designState.surface.pages.get(pageId);
                    const newPageState = Page.fromJson(newDesign.surface.pages[pageId], currentPageState, null, ctx);
                    designState.surface.pages.set(pageId, newPageState);
                }
            }

            if( workbookSheetCRUDPaths.length || workbookOtherPaths.length )
            {
                designState.spreadsheets = SpreadsheetsState.fromJson(newDesign.spreadsheets, designState.spreadsheets, null, ctx);
                openDesignCtrl.processDesignStateFromJsonCtx(ctx);
            }
            else
            {
                if( workbookViewPatchPaths.length )
                {
                    const workbookIdMask = /^\/spreadsheets\/workbooks\/(?<workbookId>[a-z0-9\-]+)\/view\/.*$/;

                    const workbookIds:string[] = [];

                    for( const path of workbookViewPatchPaths )
                    {
                        const {workbookId} = path.match(workbookIdMask)['groups'];
                        if( !workbookIds.includes(workbookId) )
                            workbookIds.push(workbookId);
                    }

                    for( const workbookId of workbookIds )
                    {
                        const currentWorkbook = designState.spreadsheets.workbooks.get(workbookId);

                        const newSpreadsheetViewState = SpreadsheetViewState.fromJson(newDesign.spreadsheets.workbooks[workbookId].view,
                            currentWorkbook.runtime.sheetsMap, currentWorkbook.view, null, ctx
                        );

                        currentWorkbook.view = newSpreadsheetViewState;
                    }
                }

                if( workbookSheetPatchPaths.length )
                {
                    const sheetMask = /^\/spreadsheets\/workbooks\/(?<workbookId>[a-z0-9\-]+)\/runtime\/sheets\/(?<sheetId>\d+)\/.*$/;

                    let sheetData:{workbookId:string, id:string}[] = [];

                    for( const path of workbookSheetPatchPaths )
                    {
                        const {workbookId, sheetId}:any = path.match(sheetMask)['groups'];
                        if( sheetId && sheetData.findIndex(sheet => sheet.workbookId === workbookId && sheet.id === sheetId) === -1 )
                            sheetData.push({workbookId, id: sheetId});
                    }

                    for( const sheet of sheetData )
                    {
                        const {workbookId, id:sheetId}:any = sheet;

                        const currentWorkbook = designState.spreadsheets.workbooks.get(workbookId);
                        const currentSheets = currentWorkbook.runtime.sheets;

                        currentSheets[sheetId] = Sheet.fromJson(newDesign.spreadsheets.workbooks[workbookId].runtime.sheets[sheetId],
                            currentWorkbook, currentWorkbook.runtime.sheets[sheetId] as Sheet, null, ctx
                        );
                    }

                    openDesignCtrl.processDesignStateFromJsonCtx(ctx);
                }
            }
            if( bindingPatchPaths.length )
            {
                designState.binding = BindingState.fromJson(newDesign.bindings, designState.spreadsheets, designState.surface, designState.binding);
            }
            // console.debug('<- patchDesign() took', performance.now() - startTime, 'ms');
        }
        else
        {
            this.setActiveDesign(newDesign);
        }

        const {surfaceSelection} = controllers.ui;

        const selectionMode = surfaceSelection.getSelectionInteractionMode();

        if( surfaceSelection.isSelectionMoving(selectionMode) || surfaceSelection.isSelectionResizing(selectionMode) )
            surfaceArea.dragSelectedBoxesBy(0, 0);

        if( surfaceBoxReparenting.isBoxReparentingEnabled() )
            surfaceBoxReparenting.enableBoxReparenting(true);
    }

    async connect(syncClient:SyncClient):Promise<LoadDesignResult>
    {
        this.syncClient = syncClient;

        const result = await this.connectImpl(syncClient);

        this.connected = true;

        return result;
    }

    protected abstract connectImpl(syncClient:SyncClient):Promise<LoadDesignResult>

    public close()
    {
        this.syncClient.disconnect();
        this.syncClient = null;
        this.connected = false;
    }
}

class SingleplayerConnection extends SyncConnection
{
    private static readonly DESIGN_EDITOR_PATTERN = /\/\d+\/\d+/;

    private designRef:DesignRef;

    private lastSyncedVersion:number;

    constructor(designRef:DesignRef)
    {
        super(designRef.projectId, designRef.designId);

        this.designRef = designRef;
    }

    async sendSyncToServer(syncEvent:SyncDesignEvent, currentDesign:any):Promise<boolean>
    {
        const openDesign = controllers.ui.openDesign.getOpenDesign();

        if( !openDesign )
        {
            logger.warn(`No open design from OpenDesignController in sendSyncToServer(); taking no action`);
            return false;
        }

        if( openDesign.designState.savePromise )
        {
            logger.warn(`Save skipped, waiting for previous save to complete`);
            openDesign.designState.hasSkippedSaves = true;
            return false;
        }

        const projectId = this.projectId;
        const designId = this.designId;
        const version = this.designRef.version;
        const designJson = JSON.stringify(currentDesign);

        const lastEdit = syncEvent.edits && syncEvent.edits[syncEvent.edits.length - 1];
        this.lastSyncedVersion = lastEdit && lastEdit.baseVersion + 1;

        const savePromise = API.designs.saveState(projectId, designId, new SaveDesignRequest(designJson, version))
            .then(action(async (response:SaveDesignResponse | ApiError):Promise<boolean> =>
            {
                openDesign.designState.savePromise = null;

                if( response instanceof ApiError )
                {
                    const path = window.location.pathname;
                    const isStillOnDesignOrFlowScreen = SingleplayerConnection.DESIGN_EDITOR_PATTERN.test(path);

                    if( response.code === 409 && isStillOnDesignOrFlowScreen )
                    {
                        controllers.ui.dialogs.showDangerReload('Oops', response.message,
                            () => location.reload(),
                            MDIcons.common.Info
                        );
                    }
                    else
                    {
                        logger.error(response.message);
                        controllers.ui.toasts.showError(new Error(response.message));
                    }

                    return false;
                }
                else if( response instanceof SaveDesignResponse )
                {
                    this.designRef.version = response.version;

                    if( openDesign.designState.hasSkippedSaves )
                    {
                        const {ui: {designSync}} = controllers;

                        openDesign.designState.hasSkippedSaves = false;

                        logger.debug('%cHas Skipped changes, re-sync design', 'color: orange');
                        return designSync.reSyncDesign();
                    }

                    return true;
                }
            }))
            .catch(error =>
            {
                openDesign.designState.savePromise = null;

                logger.error(error);
                controllers.ui.toasts.showError(error);

                return false;
            });

        openDesign.designState.savePromise = savePromise;

        // in any case => ack the edits to clean them from syncClient
        savePromise
            .catch(() => null)
            .then(() =>
            {
                if( this.lastSyncedVersion != null )
                {
                    this.syncClient.onSyncFromServer({
                        ackedVersion: this.lastSyncedVersion,
                        serverTimestamp: syncEvent.serverTimestamp,
                        clientTimestamp: new Date().getTime(),
                        edits: []
                    });
                }
            });

        return savePromise;
    }

    protected async connectImpl(syncClient:SyncClient):Promise<LoadDesignResult>
    {
        const {designName, designState, uuid, version} = await API.designs.getState(this.projectId, this.designId);

        const design = JSON.parse(designState);
        const designStateSnapshot = design.visualState ? design.visualState : design;

        this.syncClient.initialize(designStateSnapshot, version);

        return new LoadDesignResult(designName, uuid, version, designStateSnapshot);
    }
}

class MultiplayerConnection extends SyncConnection
{
    protected connectResolve:(result:LoadDesignResult) => void;
    protected connectReject:(error?:any) => void;

    constructor(projectId:number, designId:number)
    {
        super(projectId, designId);
    }

    async sendSyncToServer(syncEvent:SyncDesignEvent, currentDesign:any):Promise<boolean>
    {
        const {multiplayer} = controllers.ui;

        await multiplayer.sendEvent('sync-design', syncEvent).catch(e => false);

        return true;
    }

    protected async connectImpl(syncClient:SyncClient)
    {
        return new Promise<LoadDesignResult>((resolve, reject) =>
        {
            this.connectResolve = resolve;
            this.connectReject = reject;

            this.syncClient = syncClient;

            this._subscribeToMultiplayerEvents();
        });
    }

    private _subscribeToMultiplayerEvents()
    {
        const {multiplayer} = controllers.ui;
        const self = this;

        EventBus.listenOnce(multiplayer, {
            onDisconnected():void
            {
                self.connectReject(null);
            }
        });

        multiplayer.subscribeToEvents('init', (initEvent:InitEvent) =>
        {
            this.syncClient.initialize(initEvent.currentDesign, initEvent.currentVersion);

            const {designName, uuid, currentVersion, currentDesign} = initEvent;
            this.connectResolve(new LoadDesignResult(designName, uuid, currentVersion, currentDesign));
        });

        multiplayer.subscribeToEvents('sync-design', (syncEvent:SyncDesignEvent) =>
            this.syncClient.onSyncFromServer(syncEvent)
        );

        multiplayer.retrieveInitEvent();
    }
}
